import { PublicClientApplication } from '@azure/msal-browser';
import { IPButton, IPImage, IPStack } from 'baseComponents';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import { isAbortOrCancelError } from 'components/Notification/ErrorMessageHelper';
import { globalDesktopHeight, globalDesktopSize } from 'globalConstants';
import { globalOrgUnitId, setGlobalDefOrgLang, setGlobalOrgUnitId, setGlobalUserLang } from 'globalState';
import CurrentUser from 'models/users/currentuser';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { getIdentityProviderName, IdentityProviderType } from 'models/identity/identityProvider';
import Tenant from 'models/tenants/tenant';
import UserLanguage from 'models/users/userLanguage';
import { DarkMode } from 'models/users/userSetting';
import React, { ReactNode } from 'react';
import { toast } from 'react-toastify';
import { apiSetUserSetting } from 'services/Api/settingService';
import { apiGetTenantsForUser } from 'services/Api/tenantService';
import { apiRequest, msalConfigCommon } from 'services/Auth/authConfig';
import { IAuthProvider } from 'services/Auth/authProvider';
import { MsalAuthProvider } from 'services/Auth/msalAuthProvider';
import { clearSignInReturnPath } from 'services/Auth/signInReturnPath';
import { i18nBase } from 'services/Localization/i18n';
import logger from 'services/Logging/logService';
import { OidcAuthProvider } from 'services/Oidc/oidcAuthProvider';
import { isGoogleLoginEnabled, isOidcLoginEnabled } from 'services/Oidc/oidcConfig';
import AppError, { normalizeError } from 'utils/appError';
import { isValidGuid, normalizeGuid } from 'utils/guid';
import { getLocalStorageData, LocalStorageKeys, removeLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import {
  getSessionStorageData,
  removeSessionStorageData,
  SessionStorageKeys,
  setSessionStorageData,
} from 'utils/sessionStorage';
import AppContext, { IAppContext } from './AppContext';
import { ColorMode, UnifiedThemeProvider } from './AppDesignSystem';
import { appLogoLargeSize, getAppLogoUrl } from './appLogo';

//
// Types
//
export type AuthStateUpdate = (
  isAuthenticated?: boolean | undefined,
  user?: CurrentUser | undefined,
  isAuthInProgress?: boolean | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any | undefined,
) => void;

export type SetGlobalDataCache = (globalDataCache: GlobalDataCache) => void;

//what LocalStorageKeys.LastSeparateDirectoryOrgUnit holds: the org unit and the directory it signs in with
type SeparateDirectoryOrgUnitHint = {
  orgUnitId: string;
  identityProviderType: IdentityProviderType;
  providerTenantId: string;
};

interface IAppContextProps {
  children?: ReactNode;
}

//
// The app context: the session (identity provider, user, login answer, reference data), the shell state
// (dark mode, menu, loaders, errors, toasts) and the sign-in flows. A class on purpose: the identity providers
// call back into authStateUpdate and setGlobalDataCache, which are built once, and the silent sign-in reads
// this.state after awaits. A function provider is a later, separate change with its own manual login matrix.
//
class AppContextProvider extends React.Component<IAppContextProps, IAppContext> {
  //Microsoft Entra ID through MSAL: the provider every build has
  private readonly msalProvider: MsalAuthProvider;

  //Google Workspace through OpenID Connect, on the stages that have a Google client (Config)
  private readonly googleProvider: OidcAuthProvider | undefined;

  //the organization sign-in through an allow-listed OpenID Connect issuer
  private readonly oidcProvider: OidcAuthProvider | undefined;

  constructor(props: IAppContextProps) {
    super(props);

    this.state = {
      useDarkMode: false,
      setUseDarkMode: this.setUseDarkMode,
      isContentLoading: false,
      showContentLoader: this.showContentLoader,
      hideContentLoader: this.hideContentLoader,
      unhandledError: undefined,
      error: undefined,
      setError: this.setErrorMessage,
      showNotification: this.showNotification,
      hasScopes: this.hasScopes,
      getAccessToken: this.getAccessToken,
      setUserProfile: this.setUserProfile,
      hasMicrosoftIdentity: this.hasMicrosoftIdentity,
      adminConsentScope: this.adminConsentScope,
      loginWithProvider: this.loginWithProvider,
      isAuthenticated: false,
      isAuthInProgress: true, // set to true because we start with a silent SSO request
      user: CurrentUser.getEmptyUser(),
      refreshTenantList: this.refreshTenantList,
      isMainNavCollapsed: false,
      toggleMainNavCollapsed: this.toggleIsMainNavCollapsed,
      isOpenSideBarPanel: false,
      toggleSideBarPanel: this.toggleSideBarPanel,
      globalDataCache: new GlobalDataCache(),
      cacheMiss: this.cacheMiss,
      windowSize: globalDesktopSize,
      isMobileView: false,
      windowHeight: globalDesktopHeight,
      startQuery: undefined,
      clearStartQuery: this.clearStartQuery,
      changeUserLanguage: this.changeUserLanguage,
      login: this.login,
      logout: this.logout,
      switchOrg: this.switchOrg,
      switchOrgUnit: this.switchOrgUnit,
      isOnboardingInProgress: undefined,
      setOnboardingInProgress: this.setIsOnboardingInProgress,
    };

    this.msalProvider = new MsalAuthProvider(new PublicClientApplication(msalConfigCommon));
    this.googleProvider = isGoogleLoginEnabled() ? new OidcAuthProvider(IdentityProviderType.Google) : undefined;
    this.oidcProvider = isOidcLoginEnabled() ? new OidcAuthProvider(IdentityProviderType.Oidc) : undefined;
  }

  //
  // Identity providers
  //

  //the provider for a type, when this build can sign in with it; Microsoft Entra ID always can
  private getAuthProvider = (type: IdentityProviderType): IAuthProvider | undefined => {
    switch (type) {
      case IdentityProviderType.Entra:
        return this.msalProvider;
      case IdentityProviderType.Google:
        return this.googleProvider;
      case IdentityProviderType.Oidc:
        return this.oidcProvider;
      default:
        return undefined;
    }
  };

  //which provider starts the session on a cold load: a ?tid= deep link names an Entra directory, a ?idp= deep
  //link names its provider, otherwise the provider of the last completed sign-in. Without any of those on a
  //stage with more than one provider nobody can be chosen for the person: Microsoft is tried silently and the
  //welcome screen asks when there is no Microsoft session, instead of a popup that presumes Microsoft
  private resolveStartupProvider = (
    startQuery: string | undefined,
  ): { provider: IAuthProvider; allowInteraction: boolean } => {
    try {
      const params = new URLSearchParams(startQuery);

      if (isValidGuid(params.get('tid')?.trim() || undefined)) {
        return { provider: this.msalProvider, allowInteraction: true };
      }

      const idp = Number.parseInt(params.get('idp')?.trim() ?? '');
      if (!Number.isNaN(idp)) {
        const provider = this.getAuthProvider(idp as IdentityProviderType);
        if (provider) {
          return { provider: provider, allowInteraction: true };
        }
      }
    } catch {
      //fall through to the hint
    }

    const remembered = this.getLastIdentityProvider();
    if (remembered !== undefined) {
      return { provider: this.getAuthProvider(remembered) ?? this.msalProvider, allowInteraction: true };
    }

    return { provider: this.msalProvider, allowInteraction: !this.hasMultipleProviders };
  };

  private get hasMultipleProviders(): boolean {
    return this.googleProvider !== undefined || this.oidcProvider !== undefined;
  }

  //the provider of the signed-in user; before a sign-in the one of the last completed sign-in, else Entra
  private get activeProvider(): IAuthProvider {
    const type = this.state.isAuthenticated
      ? this.state.user.login.identityProviderType
      : (this.getLastIdentityProvider() ?? IdentityProviderType.Entra);

    return this.getAuthProvider(type) ?? this.msalProvider;
  }

  //the provider of the last completed sign-in in this browser; undefined when there was none (or a sign-out)
  private getLastIdentityProvider = (): IdentityProviderType | undefined => {
    const stored = getLocalStorageData(undefined, LocalStorageKeys.LastIdentityProvider);
    const parsed = stored !== undefined ? Number.parseInt(stored) : Number.NaN;

    return Number.isNaN(parsed) ? undefined : (parsed as IdentityProviderType);
  };

  hasMicrosoftIdentity = (): boolean => {
    return this.state.user.login.identityProviderType === IdentityProviderType.Entra;
  };

  //
  // Startup
  //
  componentDidMount() {
    try {
      //Set initial query. The landing of a Google or organization sign-in (a code and a state on the app root,
      //marked by the provider that started it) is not a start query: it is consumed by the provider, and the
      //deep link the sign-in started from, which the provider kept across its redirects, is what applies.
      let startQuery: string | undefined;
      if (OidcAuthProvider.isCallbackLanding()) {
        //a start query of an earlier load is spent
        removeSessionStorageData(SessionStorageKeys.StartQuery);
        startQuery = OidcAuthProvider.pendingReturnQuery();
        if (startQuery) {
          this.setState({ startQuery: startQuery });
        }
      } else if (window.location.search) {
        startQuery = window.location.search;
        //save the query params to the session state to apply again after auth redirect
        setSessionStorageData(SessionStorageKeys.StartQuery, startQuery);
        this.setState({ startQuery: startQuery });
      } else {
        const query = getSessionStorageData(SessionStorageKeys.StartQuery);
        if (query) {
          //apply any previously saved start query and remove it
          removeSessionStorageData(SessionStorageKeys.StartQuery);
          this.setState({ startQuery: query });
          startQuery = query;
        }
      }

      // Set the light or dark mode
      const useDarkMode = getLocalStorageData(this.state, LocalStorageKeys.DarkMode) === 'true';
      this.setState({ useDarkMode: useDarkMode });

      // Set main menu collapsed
      const mainNavCollapsed = getLocalStorageData(this.state, LocalStorageKeys.MainNavCollapsed);
      this.setState({ isMainNavCollapsed: mainNavCollapsed === 'true' });

      // Add main window resize event
      window.addEventListener('resize', this.onResize);
      this.onResize();

      //start the authentication process
      this.initializeAuth(startQuery);
    } catch (err) {
      this.setErrorMessage(err);
    }
  }

  initializeAuth = async (startQuery: string | undefined) => {
    // Initialize every provider's client (the MSAL application object, the stored Google session), so that the
    // cold-load restore below can ask any of them for its active directory
    await Promise.all([
      this.msalProvider.initialize(),
      this.googleProvider?.initialize(),
      this.oidcProvider?.initialize(),
    ]);

    // The landing of a Google or organization sign-in completes that sign-in before anything else. The provider
    // that started it marked itself before the redirect; the return url it kept sets the org unit first, so the
    // login request carries it. No default: a code without a marker of ours is not a landing.
    if (OidcAuthProvider.isCallbackLanding()) {
      const pendingType = OidcAuthProvider.pendingProviderType();
      const pendingProvider =
        pendingType === IdentityProviderType.Oidc
          ? this.oidcProvider
          : pendingType === IdentityProviderType.Google
            ? this.googleProvider
            : undefined;

      if (pendingProvider) {
        this.getTenantFromRoute(startQuery);
        await pendingProvider.handleRedirectResponse(this.authStateUpdate, this.setGlobalDataCache);

        return;
      }

      logger.debug(`No provider ${pendingType} in this build for the sign-in that is landing`);
    }

    //a load that is not a landing ends whatever sign-in was in flight (Back at the issuer): no stale marker or return url
    OidcAuthProvider.clearPendingSignIn();

    //no sign-in starts on its own on the signed-out page; a Microsoft redirect that is landing there still
    //completes (MSAL is the only provider that lands outside the root)
    const pathname = window.location.pathname;
    if (pathname === '/logout') {
      const redirectHandled = await this.msalProvider.handleRedirectResponse(
        this.authStateUpdate,
        this.setGlobalDataCache,
      );
      if (redirectHandled) {
        return;
      }

      logger.debug(`No automatic sign-in on ${pathname}`);

      this.setState({
        isAuthenticated: false,
        isAuthInProgress: false,
        error: undefined,
      });

      return;
    }

    //restore a previously selected org unit that is linked to its own directory (separate IdP) so the
    //orgunitid header is present on this cold load. A URL deep-link (?ouid=/?tid=) still takes precedence below.
    this.restoreSeparateDirectoryOrgUnit();

    // Try to login silently with the provider the deep link or the last sign-in names
    const startup = this.resolveStartupProvider(startQuery);
    await startup.provider.ssoSilent(
      this.getTenantFromRoute(startQuery),
      this.authStateUpdate,
      this.setGlobalDataCache,
      startup.allowInteraction,
    );
  };

  //
  // Restore the org unit header on a cold load for an org unit that lives in its own directory.
  // Safe by construction: the header is only restored when the active account of the org unit's identity provider
  // belongs to the org unit's directory, so the back-end will accept the login.
  //
  restoreSeparateDirectoryOrgUnit = () => {
    try {
      if (globalOrgUnitId) return; //already set (e.g. via a deep-link), do not override

      const stored = getLocalStorageData(undefined, LocalStorageKeys.LastSeparateDirectoryOrgUnit);
      if (!stored) return;

      const hint = JSON.parse(stored) as SeparateDirectoryOrgUnitHint;
      if (!isValidGuid(hint.orgUnitId) || !hint.providerTenantId) return;

      const activeDirectoryId = this.getAuthProvider(hint.identityProviderType)?.getActiveDirectoryId();
      if (activeDirectoryId?.toLowerCase() === hint.providerTenantId.toLowerCase()) {
        this.setOrgUnit(hint.orgUnitId);
        logger.debug(`Restored separate-directory org unit ${hint.orgUnitId} for directory ${hint.providerTenantId}`);
      }
    } catch {
      //no-op: never block login on restore
    }
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  clearStartQuery = () => {
    if (this.state.startQuery) {
      this.setState({ startQuery: undefined });
    }
  };

  getTenantFromRoute = (startQuery: string | undefined): string | undefined => {
    try {
      logger.debug('Start query: ' + startQuery);
      const params = new URLSearchParams(startQuery);

      //both ids travel on as the org unit header and the tid as an MSAL authority: a hand-typed link may carry
      //braces or capitals, which the API's text compare and MSAL do not take
      let urlOrgUnitParam = params.get('ouid')?.trim() || undefined;
      if (urlOrgUnitParam && isValidGuid(urlOrgUnitParam)) {
        urlOrgUnitParam = normalizeGuid(urlOrgUnitParam);
        this.setOrgUnit(urlOrgUnitParam);
        this.clearStartQuery();
      } else {
        urlOrgUnitParam = undefined;
      }

      let urlTenantParam = params.get('tid')?.trim() || undefined;
      if (urlTenantParam && isValidGuid(urlTenantParam)) {
        urlTenantParam = normalizeGuid(urlTenantParam);

        //the tid is the Entra tenant id. Without an org unit, the OU is set to the same id as the top level tenant
        //to signal the API to log into the top level tenant
        if (!urlOrgUnitParam) {
          this.setOrgUnit(urlTenantParam);
          this.clearStartQuery();
        }

        return urlTenantParam;
      }

      return undefined;
    } catch {
      return undefined;
    }
  };

  //
  // Loader functions
  //
  showContentLoader = () => this.setState({ isContentLoading: true });

  hideContentLoader = () => this.setState({ isContentLoading: false });

  //
  // Light and dark mode functions
  //
  // The database owns the theme (phase 12 W2): a toggle writes PUT settings/user so it follows the user to
  // another browser, and keeps the localStorage key as a mirror so the pre-login render already paints in the
  // right theme. A failed write is reported and the mirror stays as the user set it.
  setUseDarkMode = (useDarkMode: boolean) => {
    setLocalStorageData(this.state, LocalStorageKeys.DarkMode, useDarkMode.toString(), false);
    this.state.globalDataCache.userSettings.set(DarkMode, useDarkMode);
    this.setState({ useDarkMode: useDarkMode });

    this.saveDarkModeAsync(useDarkMode, true);
  };

  //isExplicit: the user just flipped the switch, so a failure is theirs to see; the one-time seed is
  //background work and only logs, because nobody asked for it at that moment
  private saveDarkModeAsync = async (useDarkMode: boolean, isExplicit: boolean = false) => {
    if (!this.state.isAuthenticated) {
      return;
    }

    try {
      const accessToken = await this.activeProvider.getAccessToken(apiRequest.scopes);
      await apiSetUserSetting(DarkMode, useDarkMode, accessToken);
    } catch (err) {
      //a failed seed or toggle must never break a sign-in: the mirror keeps the theme on this browser
      if (isExplicit) {
        //the switch lives in the settings drawer, so the message goes to the bottom-center container
        this.showNotification(i18nBase.t('settings:Theme.SaveFailed'), true, false, true);
      } else {
        logger.debug('The dark mode seed could not be saved', err);
      }
    }
  };

  // The theme of the login answer: the stored row decides, and when there is none the mirror of this browser
  // seeds the database once, so a user who had a theme before this release keeps it without re-toggling (D9).
  private applyDarkModeFromCache = (globalDataCache: GlobalDataCache) => {
    const mirror = getLocalStorageData(this.state, LocalStorageKeys.DarkMode) === 'true';

    if (globalDataCache.userSettings.has(DarkMode)) {
      const stored = globalDataCache.userSettings.get(DarkMode);
      setLocalStorageData(this.state, LocalStorageKeys.DarkMode, stored.toString(), false);
      this.setState({ useDarkMode: stored });

      return;
    }

    this.setState({ useDarkMode: mirror });

    if (mirror) {
      globalDataCache.userSettings.set(DarkMode, mirror);
      //the login tail fills the cache BEFORE it marks the session authenticated and sets the org unit header
      //(loginProfile.completeLoginProfile), so the seed cannot call the API here: it waits for authStateUpdate
      this.pendingDarkModeSeed = mirror;
    }
  };

  //the one-time D9 seed, held until the session is authenticated and the org unit of the sign-in is in place
  private pendingDarkModeSeed: boolean | undefined;

  private flushPendingDarkModeSeed = () => {
    const seed = this.pendingDarkModeSeed;
    if (seed === undefined) {
      return;
    }

    this.pendingDarkModeSeed = undefined;
    this.saveDarkModeAsync(seed);
  };

  //
  // Onboarding state
  //
  setIsOnboardingInProgress = (inProgress: boolean) => {
    this.setState({ isOnboardingInProgress: inProgress });
  };

  //
  // Authentication service wrapper functions
  //
  authStateUpdate = (
    isAuthenticated?: boolean | undefined,
    user?: CurrentUser | undefined,
    isAuthInProgress?: boolean | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any | undefined,
  ) => {
    //when authentication is done and we have a user
    if (user && !isAuthInProgress) {
      //apply the user language
      logger.debug('Setting user language to: ' + (user.login.userLanguageCode ?? user.language.code));
      if (user.login.userLanguageCode) {
        // Language setting from database overrides the account setting
        user.language = new UserLanguage(user.login.userLanguageCode);
      }
      i18nBase.changeLanguage(user.language.code);

      // set language headers
      this.setLanguages(user);

      //update the global state for the organizational unit when user has logged into one
      if (user.login.isOrgUnit) {
        this.setOrgUnit(user.login.tenantId);
      } else {
        this.setOrgUnit(undefined);
        removeLocalStorageData(undefined, LocalStorageKeys.LastSeparateDirectoryOrgUnit);
      }

      //remember the identity provider of a completed sign-in, so the next cold load starts with the same one
      if (isAuthenticated === true) {
        setLocalStorageData(
          undefined,
          LocalStorageKeys.LastIdentityProvider,
          user.login.identityProviderType.toString(),
          false,
        );
      }

      // Update the state with 1 statement, otherwise timing issues will arise
      const newState = {
        isAuthenticated: isAuthenticated !== undefined ? isAuthenticated : this.state.isAuthenticated,
        isAuthInProgress: isAuthInProgress !== undefined ? isAuthInProgress : this.state.isAuthInProgress,
        user: user,
        error: error !== undefined ? error : this.state.error,
        isOnboardingInProgress:
          this.state.isOnboardingInProgress !== undefined
            ? this.state.isOnboardingInProgress
            : user.login.isOnboardingInProgress,
      };

      //the session is authenticated and the org unit is set from here on, so the held dark-mode seed (D9) can
      //reach the API; the callback runs once the state carries isAuthenticated
      this.setState(newState, this.flushPendingDarkModeSeed);
    } else if (isAuthInProgress) {
      // Only set isAuthInProgress to true, keep the user and isAuthenticated state until we have a result
      const newState = {
        isAuthenticated: isAuthenticated !== undefined ? isAuthenticated : this.state.isAuthenticated,
        isAuthInProgress: true,
        user: user !== undefined ? user : this.state.user,
        error: error !== undefined ? error : this.state.error,
      };

      this.setState(newState);
    }
  };

  //the user menu's "sign in with another account": the provider of the signed-in person shows its chooser. The
  //sign-in buttons of the welcome screen name their provider through loginWithProvider instead
  login = async () => {
    return await this.loginWithProvider(this.activeProvider.providerType, undefined, true);
  };

  loginWithProvider = async (type: IdentityProviderType, directoryId?: string, switchAccount: boolean = false) => {
    const provider = this.getAuthProvider(type);
    if (!provider) {
      logger.debug(`Identity provider ${type} is not available in this build`);

      return;
    }

    this.setOrgUnit(undefined);

    return await provider.login(directoryId, this.authStateUpdate, this.setGlobalDataCache, switchAccount);
  };

  logout = async () => {
    this.setOrgUnit(undefined);
    //the provider is read before the hint goes, so the sign-out reaches the provider the user signed in with
    const provider = this.activeProvider;
    removeLocalStorageData(undefined, LocalStorageKeys.LastIdentityProvider);
    //the org unit hint is provider-neutral, and the next sign-in in this browser may be someone else's
    removeLocalStorageData(undefined, LocalStorageKeys.LastSeparateDirectoryOrgUnit);
    clearSignInReturnPath();

    return await provider.logout(this.authStateUpdate);
  };

  switchOrg = async (item: Tenant) => {
    //the organization decides which identity provider signs the user in to it; one this build cannot sign in
    //with is said out loud, the user clicked and would otherwise see nothing happen
    const provider = this.getAuthProvider(item.identityProviderType);
    if (!provider) {
      logger.debug(
        `Identity provider ${item.identityProviderType} of tenant ${item.id} is not available in this build`,
      );
      //the namespace is loaded by the components that use it, not by this class
      await i18nBase.loadNamespaces('identityproviders');
      this.showNotification(
        i18nBase.t('identityproviders:Errors.ProviderNotAvailable', {
          tenant: item.name,
          provider: getIdentityProviderName(item.identityProviderType),
        }),
        true,
      );

      return;
    }

    //set the org unit to the id of the organization: for a top level organization the back-end then logs into it
    //instead of into the last known org unit
    this.setOrgUnit(item.id);

    await provider.switchTenant(item, this.authStateUpdate, this.setGlobalDataCache);
  };

  switchOrgUnit = async (item: Tenant) => {
    this.setOrgUnit(item.id);
    await this.activeProvider.setUserProfile(this.authStateUpdate, this.setGlobalDataCache);
  };

  setOrgUnit = (orgUnitId: string | undefined) => {
    setGlobalOrgUnitId(orgUnitId || undefined);
  };

  changeUserLanguage = async (code: string): Promise<string> => {
    const newUser = this.state.user;

    if (!UserLanguage.getSupportedLanguages().has(code)) {
      //revert to the fallback language
      newUser.language = new UserLanguage(UserLanguage.getFallBack());
    } else {
      newUser.language = new UserLanguage(code);
    }

    //inform the localization service to change the language
    await i18nBase.changeLanguage(newUser.language.codeWithCulture);

    //re-initialize the global language variables for sending in headers
    this.setLanguages(newUser);

    //update the global state
    this.setState({ user: newUser });

    return newUser.language.code;
  };

  setLanguages = (user: CurrentUser) => {
    setGlobalUserLang(user.language.code);
    setGlobalDefOrgLang(user.login.defLanguageCode || undefined);
  };

  adminConsentScope = (clientId: string, scopes: string[], redirectUrl: string, stateSuffix?: string) => {
    this.activeProvider.adminConsentScope(clientId, scopes.join(' '), this.authStateUpdate, redirectUrl, stateSuffix);
  };

  hasScopes = async (scopes: string[]) => {
    return await this.activeProvider.hasScopes(scopes);
  };

  getAccessToken = async (scopes: string[]) => {
    return await this.activeProvider.getAccessToken(scopes);
  };

  setUserProfile = async () => {
    return await this.activeProvider.setUserProfile(this.authStateUpdate, this.setGlobalDataCache);
  };

  refreshTenantList = async () => {
    try {
      const newUser = this.state.user.clone();
      const accessToken = await this.activeProvider.getAccessToken(apiRequest.scopes);
      newUser.login.tenants = await apiGetTenantsForUser(accessToken);
      this.setState({ user: newUser });
    } catch (err) {
      this.setErrorMessage(err);
    }
  };

  //
  // Global cache functions
  //
  setGlobalDataCache = (globalDataCache: GlobalDataCache) => {
    //initialize the global data cache retrieved from the back-end at login
    //1. apply the IAppContext so that the cache can use API calls
    globalDataCache.setAppContext(this.state);
    //2. clone the cache so that we can use it in the state
    const newGlobalDataCache = globalDataCache.clone();
    this.setState({ globalDataCache: newGlobalDataCache });
    //3. the theme of the signed-in user: the database owns it, this browser's key is only the paint mirror
    this.applyDarkModeFromCache(newGlobalDataCache);
  };

  cacheMiss = async (count: number) => {
    //disable cache refresh. this is not possible in a React rendering phase.
  };

  //
  // Global error handling
  //
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    let newUnhandledError = this.getAppError(error);

    if (!newUnhandledError) {
      newUnhandledError = new AppError('Unknown error');
    }

    //a chunk that cannot be loaded after a deployment is resolved by loading the new version of the app
    const message = newUnhandledError.message ?? '';
    const resolveByReload =
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('error loading dynamically imported module') ||
      (message.includes('Loading chunk') && message.includes('failed'));

    if (resolveByReload) {
      window.location.reload();
    } else {
      newUnhandledError.code = '999';
      newUnhandledError.debug = errorInfo.componentStack || '';

      this.setState({
        unhandledError: newUnhandledError,
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setErrorMessage = (error: string | any) => {
    const appError = this.getAppError(error);
    if (isAbortOrCancelError(appError)) {
      return;
    }

    this.setState({
      error: appError,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAppError = (error: string | any): AppError | undefined => {
    const appError = normalizeError(error);

    //when this instance is the same as the instance in the state, return the state
    if (appError?.isEqual(this.state.error)) {
      return this.state.error;
    }

    return appError;
  };

  showNotification = (
    msg: string | undefined,
    isError: boolean = false,
    isWarning: boolean = false,
    isBottom: boolean = false,
    link?: { text: string; onClick: () => void },
  ) => {
    const text = msg || i18nBase.t('translation:Errors.Unknown.Message');
    const containerId = isBottom ? 'app-bottom' : 'app';
    const content = link ? (
      <span>
        {text}{' '}
        <IPButton appearance="transparent" onClick={link.onClick}>
          {link.text}
        </IPButton>
      </span>
    ) : (
      text
    );

    if (isError) {
      toast.error(content, { containerId: containerId });
    } else if (isWarning) {
      toast.warn(content, { containerId: containerId });
    } else {
      toast.success(content, { containerId: containerId });
    }
  };

  //
  // Panel functions
  //
  toggleIsMainNavCollapsed = () => {
    this.setState({ isMainNavCollapsed: !this.state.isMainNavCollapsed });
  };

  toggleSideBarPanel = () => {
    this.setState({ isOpenSideBarPanel: !this.state.isOpenSideBarPanel });
  };

  //
  // Mobile functions
  //
  getWindowWidth = (): number => {
    return Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
  };

  getWindowHeight = (): number => {
    return Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
  };

  onResize = () => {
    try {
      const width = this.getWindowWidth();
      const height = this.getWindowHeight();
      this.setState({
        windowSize: width,
        windowHeight: height,
        isMobileView: width < globalDesktopSize,
      });
    } catch {
      //ignore
    }
  };

  //
  // Main render
  //
  render() {
    if (this.state.unhandledError) {
      return (
        <AppContext.Provider value={this.state}>
          <UnifiedThemeProvider mode={this.state.useDarkMode ? ColorMode.Dark : ColorMode.Light}>
            <IPStack horizontalAlign="center" tokens={{ childrenGap: 24, padding: 24 }}>
              <ErrorMessage error={this.state.unhandledError} />
              <IPImage src={getAppLogoUrl()} width={appLogoLargeSize} height={appLogoLargeSize} />
            </IPStack>
          </UnifiedThemeProvider>
        </AppContext.Provider>
      );
    }

    return <AppContext.Provider value={this.state}>{this.props.children}</AppContext.Provider>;
  }
}

export default AppContextProvider;
